/**
 * ===========================================================================================
 *  THE SEAM — the one change that points the calendar at the real API.
 *
 *  Two implementations, interchangeable by construction:
 *    ./fixture-calendar-api.ts   in-memory, one seeded month, what the screen runs against today
 *    ./http-calendar-api.ts      one `fetch`, bearer token, cross-origin to moonx.art/authors-api
 *
 *  IT DEFAULTS TO FIXTURES FOR THE SAME REASON THE CABINET'S OWN SEAM DOES: with no API behind a
 *  build, pointing at the network by default would mean every load is an error state.
 *  `VITE_AUTHORS_API_LIVE=true` flips it, per build, and the flag is READ FROM THE CABINET'S OWN
 *  seam (`isLiveApiConfigured`) rather than re-read here, so the two halves of this app cannot
 *  disagree about whether the API exists.
 *
 *  ★ AND IT IS INSTALLED AS THE DEFAULT rather than provided, because the cabinet's router mounts
 *  `CABINET_ROUTES[].Component` with no props and inside no provider (`web/authors/src/router.tsx`,
 *  the `cabinetRoutes` map). A screen that required a provider would be a white page the moment it
 *  joined that list. `<CalendarApiProvider>` still overrides it, which is what a test uses.
 * ===========================================================================================
 */
import { accessToken } from '../../auth/session/access-token';
import { isLiveApiConfigured } from '../../cabinet/api';
import { createFixtureCalendarApi } from './fixture-calendar-api';
import { createHttpCalendarApi } from './http-calendar-api';
import { setDefaultCalendarApi } from './calendar-api';
import type { AccessTokenSource } from './http-calendar-api';
import type { FixtureCalendarApiOptions } from './fixture-calendar-api';
import type { CalendarApi } from './calendar-api';

export interface CalendarApiSourceOptions {
  /** How the bearer token is obtained. Omitted, it is the app's one token source - the auth
   *  client's current session (`features/auth/session/access-token.ts`). A test passes its own. */
  readonly getAccessToken?: AccessTokenSource;
  /** Force one or the other, for a test and for a screenshot of a state nobody can otherwise reach. */
  readonly force?: 'fixtures' | 'http';
  readonly fixture?: FixtureCalendarApiOptions;
}

export function createCalendarApi(options: CalendarApiSourceOptions = {}): CalendarApi {
  const useHttp =
    options.force === 'http' || (options.force !== 'fixtures' && isLiveApiConfigured());

  if (!useHttp) {
    return createFixtureCalendarApi(options.fixture ?? {});
  }

  // BEARER-ONLY WITH NO COOKIES across origins (adr/0002): there is no ambient credential, so the
  // token source is defaulted rather than skipped. The default instance below is built with no
  // arguments, which is what used to make a live build throw here.
  return createHttpCalendarApi({ getAccessToken: options.getAccessToken ?? accessToken });
}

/**
 * One instance for the whole app, built lazily on first use.
 *
 * Lazily, and not at module load: nothing about which client to build is worth deciding before a
 * screen asks, and a throw during module evaluation - the fixture's or the network's - is a blank
 * page with a stack trace instead of a screen with an error state.
 */
let instance: CalendarApi | undefined;

setDefaultCalendarApi((): CalendarApi => {
  instance ??= createCalendarApi();
  return instance;
});
